import { collection, deleteDoc, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Competition from '../types/Competition';

export const useCompetitionMutators = () => {
  const { isGod } = useAuth();
  const { userState } = useData();

  const addCompetition = async (competition: Competition): Promise<Competition | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found');
      return null;
    }

    if (!isGod) {
      console.error('Permission denied: only god can add competitions');
      return null;
    }

    try {
      const compRef = competition.itemId
        ? doc(firestore, `competition/${competition.itemId}`)
        : doc(collection(firestore, 'competition'));

      const newCompetition = {
        ...competition,
        creatorId: userState.userProfile.itemId,
        createTime: Timestamp.now(),
        itemId: compRef.id,
      };

      const cleaned = JSON.parse(JSON.stringify(newCompetition));
      await setDoc(compRef, cleaned);
      return cleaned;
    } catch (error) {
      console.error('Error adding competition:', error);
      return null;
    }
  };

  const updateCompetition = async (competition: Partial<Competition>): Promise<void> => {
    if (!competition.itemId) {
      console.error('Competition itemId not found');
      return;
    }

    if (!isGod) {
      console.error('Permission denied: only god can update competitions');
      return;
    }

    try {
      const compRef = doc(firestore, `competition/${competition.itemId}`);
      const existing = await getDoc(compRef);
      if (!existing.exists()) {
        console.error('Competition not found');
        return;
      }

      const updated = { ...existing.data(), ...competition };
      const cleaned = JSON.parse(JSON.stringify(updated));
      await setDoc(compRef, cleaned);
    } catch (error) {
      console.error('Error updating competition:', error);
    }
  };

  const deleteCompetition = async (competition: Competition): Promise<void> => {
    if (!isGod) {
      console.error('Permission denied: only god can delete competitions');
      return;
    }

    if (!competition.itemId) {
      console.error('Competition itemId not found');
      return;
    }

    try {
      await deleteDoc(doc(firestore, `competition/${competition.itemId}`));
    } catch (error) {
      console.error('Error deleting competition:', error);
    }
  };

  return { addCompetition, updateCompetition, deleteCompetition };
};
